/**
 * Google Analytics 4 유틸리티
 *
 * GA 측정 ID가 없으면 모든 함수가 no-op으로 동작합니다.
 * 환경변수: VITE_GA_MEASUREMENT_ID (예: G-XXXXXXXXXX)
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined

/** GA가 활성화되어 있는지 */
export const isGAEnabled = !!GA_ID

/** GA 초기화 — gtag.js 동적 로드 + config */
export function initGA(): void {
  if (!isGAEnabled || !GA_ID || typeof window === 'undefined') return
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)
  window.dataLayer = window.dataLayer || []
  window.gtag = function () { window.dataLayer!.push(arguments) }
  window.gtag('js', new Date())
  window.gtag('config', GA_ID, { send_page_view: false })
}

/** gtag 래퍼 */
function gtag(...args: any[]) {
  if (!isGAEnabled || !window.gtag) return
  window.gtag(...args)
}

/** 페이지뷰 전송 (SPA 라우터 변경 시 호출) */
export function trackPageView(path: string, title?: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  })
}

/** 커스텀 이벤트 전송 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  gtag('event', eventName, params)
}

// === 사전 정의 이벤트 ===

/** 시설 검색 */
export function trackSearchFacility(keyword?: string, region?: string, filterCount?: number) {
  trackEvent('search_facility', { keyword, region, filter_count: filterCount })
}

/** 시설 상세 조회 */
export function trackViewFacilityDetail(facilityId: number, facilityType?: string) {
  trackEvent('view_facility_detail', { facility_id: facilityId, facility_type: facilityType })
}

/** AI 추천 시작 */
export function trackStartRecommendation(careGrade?: string) {
  trackEvent('start_recommendation', { care_grade: careGrade })
}

/** AI 추천 완료 */
export function trackCompleteRecommendation(resultCount: number, topScore?: number) {
  trackEvent('complete_recommendation', { result_count: resultCount, top_score: topScore })
}

/** 상담 요청 */
export function trackRequestConsultation(facilityId: number) {
  trackEvent('request_consultation', { facility_id: facilityId })
}

/** 비용계산기 계산 완료 */
export function trackCalculateCost(facilityType?: string, totalMin?: number, totalMax?: number) {
  trackEvent('calculate_cost_complete', {
    facility_type: facilityType,
    total_min: totalMin,
    total_max: totalMax,
  })
}

/** 시설 전화 클릭 (실질 전환) */
export function trackCallFacility(facilityId: number) {
  trackEvent('call_facility_click', { facility_id: facilityId })
}

/** 북마크 추가 (리텐션 선행 지표) */
export function trackAddBookmark(facilityId: number) {
  trackEvent('add_bookmark', { facility_id: facilityId })
}

/** 통합돌봄 계산기 시작 — 어느 모드를 고르는지 본다 */
export function trackStartCareCalculator(mode: 'QUICK' | 'FULL') {
  trackEvent('care_calculator_start', { mode })
}

/**
 * 통합돌봄 계산기 진행률 — 25/50/75% 지점에서 한 번씩.
 *
 * 문답이 단일 URL 이라 페이지뷰로는 어디서 이탈했는지 알 수 없다.
 * 이 이벤트가 있어야 GA4 에서 시작→25→50→75→완료 퍼널이 그려진다.
 */
export function trackCareCalculatorProgress(mode: 'QUICK' | 'FULL', percent: 25 | 50 | 75) {
  trackEvent('care_calculator_progress', { mode, percent })
}

/**
 * 통합돌봄 계산기 완료.
 *
 * 답변 자체는 건강정보(민감정보)라 보내지 않는다. 개인을 특정할 수 없는
 * 집계용 값 — 모드, 등급 구간, 응답/미응답 수 — 만 보낸다.
 */
export function trackCompleteCareCalculator(params: {
  mode: 'QUICK' | 'FULL'
  band: string
  answeredCount: number
  unknownCount: number
}) {
  trackEvent('care_calculator_complete', {
    mode: params.mode,
    band: params.band,
    answered_count: params.answeredCount,
    unknown_count: params.unknownCount,
  })
}

/** 자가진단 시작 */
export function trackStartSelfAssessment() {
  trackEvent('start_self_assessment')
}

/** 자가진단 완료 */
export function trackCompleteSelfAssessment(estimatedGrade: string, totalScore: number) {
  trackEvent('complete_self_assessment', { estimated_grade: estimatedGrade, total_score: totalScore })
}

/** 회원가입 */
export function trackSignup(method: string) {
  trackEvent('signup', { method })
}

/** 로그인 */
export function trackLogin(method: string) {
  trackEvent('login', { method })
}

/** 정보 확인 페이지 진입 */
export function trackSignupProfilePageViewed() {
  trackEvent('signup_profile_page_viewed')
}

/** 정보 확인 단계 완료 */
export function trackSignupProfileCompleted(params: {
  emailChanged: boolean
  birthDateProvided: boolean
  genderProvided: boolean
}) {
  trackEvent('signup_profile_completed', {
    email_changed: params.emailChanged,
    birth_date_provided: params.birthDateProvided,
    gender_provided: params.genderProvided,
  })
}

/** 정보 확인 단계 이탈 (라우트 전환) */
export function trackSignupProfileAbandoned() {
  trackEvent('signup_profile_abandoned')
}
